import { ImportFieldDefinition, ColumnMapping } from "../DataImport.types";

/**
 * Takes raw CSV rows and a column mapping, then applies:
 * 1. Column mapping (CSV column -> target field)
 * 2. Type coercion (string -> number, date parsing, etc.)
 * 3. Resolver execution for lookup fields
 * 4. Default value injection for unmapped/empty fields
 *
 * Returns an array of resolved records ready for validation.
 */
export async function resolveRowData(
  csvRows: Record<string, string>[],
  fields: ImportFieldDefinition[],
  columnMapping: ColumnMapping
): Promise<Record<string, unknown>[]> {
  const resolvedRows: Record<string, unknown>[] = [];

  for (const csvRow of csvRows) {
    const resolvedRow: Record<string, unknown> = {};

    for (const field of fields) {
      const csvColumn = columnMapping[field.key];
      const rawValue = csvColumn ? csvRow[csvColumn] : undefined;
      const isEmpty =
        rawValue === undefined || rawValue === null || rawValue === "";

      if (isEmpty) {
        resolvedRow[field.key] =
          field.defaultValue !== undefined ? field.defaultValue : null;
        continue;
      }

      // If field has a custom resolver, use it
      if (field.resolver) {
        try {
          const resolved = await field.resolver(rawValue, csvRow);
          resolvedRow[field.key] = resolved !== undefined ? resolved : null;
        } catch {
          resolvedRow[field.key] = null;
        }
        continue;
      }

      // Otherwise apply type coercion
      resolvedRow[field.key] = coerceValue(rawValue, field);
    }

    resolvedRows.push(resolvedRow);
  }

  return resolvedRows;
}

function coerceValue(rawValue: string, field: ImportFieldDefinition): unknown {
  switch (field.type) {
    case "number": {
      const num = Number(rawValue);
      return isNaN(num) ? rawValue : num;
    }

    case "boolean": {
      const lower = rawValue.toLowerCase().trim();
      if (["true", "1", "yes", "y"].includes(lower)) return true;
      if (["false", "0", "no", "n"].includes(lower)) return false;
      return rawValue;
    }

    case "date": {
      return rawValue;
    }

    case "select": {
      // For select fields without a resolver, try matching by label or value
      if (field.options) {
        const lower = rawValue.toLowerCase().trim();
        const match = field.options.find(
          (opt) =>
            String(opt.value).toLowerCase() === lower ||
            opt.label.toLowerCase() === lower
        );
        if (match) return match.value;
      }
      return rawValue;
    }

    case "text":
    default:
      return rawValue;
  }
}
